<template>
  <div class="py-4 container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-12">
        <h5 class="mb-0">{{ $t('analytics.timesheetAnalytics') }}</h5>
        <p class="mb-0 text-sm text-secondary">{{ $t('analytics.timesheetAnalyticsSubtitle') }}</p>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <div v-else>
      <!-- Row 1: Total Hours + Overtime Hours -->
      <div class="row">
        <!-- Total Weekly Hours -->
        <div class="col-lg-6 col-12 mb-4">
          <div class="card z-index-2">
            <div class="card-header p-3 pt-2">
              <div class="icon icon-lg icon-shape bg-gradient-info shadow-info text-center border-radius-xl mt-n4 me-3 float-start">
                <i class="material-icons opacity-10 graph-icon-fit">schedule</i>
              </div>
              <div class="d-block">
                <p class="text-sm mb-0 text-capitalize text-secondary">{{ $t('analytics.weeklyTotalHours') }}</p>
                <h4 class="mb-0 font-weight-bolder">{{ lastWeekHours }} {{ $t('timeRegistration.hrs') }}</h4>
              </div>
            </div>
            <div class="p-3 card-body">
              <div class="chart">
                <canvas id="weekly-hours-chart" class="chart-canvas" height="250"></canvas>
              </div>
            </div>
          </div>
        </div>

        <!-- Overtime Hours -->
        <div class="col-lg-6 col-12 mb-4">
          <div class="card z-index-2">
            <div class="card-header p-3 pt-2">
              <div class="icon icon-lg icon-shape bg-gradient-warning shadow-warning text-center border-radius-xl mt-n4 me-3 float-start">
                <i class="material-icons opacity-10 graph-icon-fit">timer</i>
              </div>
              <div class="d-block">
                <p class="text-sm mb-0 text-capitalize text-secondary">{{ $t('analytics.weeklyOvertimeHours') }}</p>
                <h4 class="mb-0 font-weight-bolder">{{ lastWeekOvertime }} {{ $t('timeRegistration.hrs') }}</h4>
              </div>
            </div>
            <div class="p-3 card-body">
              <div class="chart">
                <canvas id="weekly-overtime-chart" class="chart-canvas" height="250"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 2: Registration Compliance -->
      <div class="row">
        <div class="col-12 mb-4">
          <div class="card z-index-2">
            <div class="card-header p-3 pt-2">
              <div class="icon icon-lg icon-shape bg-gradient-success shadow-success text-center border-radius-xl mt-n4 me-3 float-start">
                <i class="material-icons opacity-10 graph-icon-fit">how_to_reg</i>
              </div>
              <div class="d-block">
                <p class="text-sm mb-0 text-capitalize text-secondary">{{ $t('analytics.registrationCompliance') }}</p>
                <h4 class="mb-0 font-weight-bolder">{{ lastWeekCompliance }} / {{ lastWeekTotal }} {{ $t('analytics.employeesRegistered') }}</h4>
              </div>
            </div>
            <div class="p-3 card-body">
              <div class="chart">
                <canvas id="compliance-chart" class="chart-canvas" height="200"></canvas>
              </div>
              <div class="d-flex gap-4 justify-content-center mt-2 text-sm">
                <span><span class="legend-dot" style="background:#4caf50"></span> {{ $t('analytics.fullyRegistered') }}</span>
                <span><span class="legend-dot" style="background:#f44336"></span> {{ $t('analytics.notFullyRegistered') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 3: Missing today + Late clock-ins -->
      <div class="row">
        <!-- Missing registrations today -->
        <div class="col-lg-5 col-12 mb-4">
          <div class="card">
            <div class="card-header p-3 pb-0">
              <h6 class="mb-0">
                <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">person_off</i>
                {{ $t('analytics.missingToday') }}
              </h6>
              <p class="text-xs text-secondary mb-0">{{ $t('analytics.missingTodaySubtitle') }}</p>
            </div>
            <div class="p-3 card-body">
              <p v-if="missingRows.length === 0" class="text-sm text-secondary mb-0">{{ $t('analytics.nobodyMissing') }}</p>
              <el-table v-else :data="missingRows" size="small" stripe max-height="320">
                <el-table-column prop="userName" :label="$t('reports.employee')" min-width="160" />
                <el-table-column :label="$t('analytics.scheduledHours')" align="right" width="120">
                  <template #default="{ row }">{{ Number(row.scheduledHours).toFixed(1) }}</template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>

        <!-- Late clock-ins -->
        <div class="col-lg-7 col-12 mb-4">
          <div class="card">
            <div class="card-header p-3 pb-0 d-flex flex-wrap justify-content-between align-items-start">
              <div>
                <h6 class="mb-0">
                  <i class="material-icons me-1" style="font-size:1.1rem;vertical-align:middle">schedule_send</i>
                  {{ $t('analytics.lateClockIns') }}
                </h6>
                <p class="text-xs text-secondary mb-0">{{ $t('analytics.lateClockInsSubtitle') }}</p>
              </div>
              <div class="d-flex align-items-center gap-2">
                <el-date-picker v-model="lateFromDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" size="small" style="width:130px" @change="fetchLateClockIns" />
                <span class="text-secondary">–</span>
                <el-date-picker v-model="lateToDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" size="small" style="width:130px" @change="fetchLateClockIns" />
              </div>
            </div>
            <div class="p-3 card-body">
              <p v-if="lateRows.length === 0" class="text-sm text-secondary mb-0">{{ $t('analytics.noLateClockIns') }}</p>
              <el-table v-else :data="lateRows" size="small" stripe max-height="320">
                <el-table-column prop="userName" :label="$t('reports.employee')" min-width="150" />
                <el-table-column :label="$t('timeRegistration.date')" width="100">
                  <template #default="{ row }">{{ formatDate(row.date) }}</template>
                </el-table-column>
                <el-table-column :label="$t('analytics.firstClockIn')" width="100" align="center">
                  <template #default="{ row }">{{ formatTime(row.firstClockIn) }}</template>
                </el-table-column>
                <el-table-column :label="$t('analytics.minutesLate')" width="110" align="right">
                  <template #default="{ row }"><span class="text-warning fw-bold">{{ row.minutesLate }} {{ $t('timeRegistration.minutesShort') }}</span></template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import Chart from "chart.js/auto";
import { ElTable, ElTableColumn, ElDatePicker } from "element-plus";

/**
 * TimesheetAnalytics — Admin-only view showing 13-week timesheet analytics.
 * Charts: total work hours per week, overtime hours per week,
 * and employee registration compliance (≥5 days/week = fully registered).
 */
export default {
  name: "TimesheetAnalytics",
  components: { ElTable, ElTableColumn, ElDatePicker },
  data() {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 6);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return {
      loading: true,
      weeklyHours: [],
      weeklyOvertime: [],
      registrationCompliance: [],
      hoursChart: null,
      overtimeChart: null,
      complianceChart: null,
      missingRows: [],
      lateRows: [],
      lateFromDate: iso(weekAgo),
      lateToDate: iso(today),
    };
  },
  computed: {
    lastWeekHours() {
      const last = this.weeklyHours.find((w) => w.isLastWeek);
      return last ? last.hours.toFixed(1) : "–";
    },
    lastWeekOvertime() {
      const last = this.weeklyOvertime.find((w) => w.isLastWeek);
      return last ? last.hours.toFixed(1) : "–";
    },
    lastWeekCompliance() {
      const last = this.registrationCompliance.find((w) => w.isLastWeek);
      return last ? last.compliant : "–";
    },
    lastWeekTotal() {
      const last = this.registrationCompliance.find((w) => w.isLastWeek);
      return last ? last.compliant + last.nonCompliant : "–";
    },
  },
  async mounted() {
    await Promise.allSettled([this.fetchData(), this.fetchMissing(), this.fetchLateClockIns()]);
  },
  beforeUnmount() {
    this.hoursChart?.destroy();
    this.overtimeChart?.destroy();
    this.complianceChart?.destroy();
  },
  methods: {
    formatDate(dt) {
      const d = new Date(dt);
      return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
    },
    formatTime(dt) {
      const d = new Date(dt);
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },
    async fetchMissing() {
      try {
        const r = await api.get("/Timesheet/missing-registrations");
        this.missingRows = r.data || [];
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    async fetchLateClockIns() {
      try {
        const r = await api.get(`/Timesheet/late-clockins?dateFrom=${this.lateFromDate}&dateTo=${this.lateToDate}`);
        this.lateRows = r.data || [];
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    async fetchData() {
      try {
        const response = await api.get("/Analytics/Timesheet/WeeklySummary");
        this.weeklyHours = response.data.weeklyHours || [];
        this.weeklyOvertime = response.data.weeklyOvertime || [];
        this.registrationCompliance = response.data.registrationCompliance || [];
        this.loading = false;
        this.$nextTick(() => {
          this.buildHoursChart();
          this.buildOvertimeChart();
          this.buildComplianceChart();
        });
      } catch (e) {
        console.error("TimesheetAnalytics fetchData error:", e); // eslint-disable-line no-console
        this.loading = false;
      }
    },
    buildHoursChart() {
      const ctx = document.getElementById("weekly-hours-chart");
      if (!ctx) return;
      const labels = this.weeklyHours.map((w) => w.weekLabel);
      const data = this.weeklyHours.map((w) => w.hours);
      const colors = this.weeklyHours.map((w) => w.isLastWeek ? "#1a73e8" : "#90caf9");
      this.hoursChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [{
            label: this.$t("analytics.weeklyTotalHours"),
            data,
            backgroundColor: colors,
            borderRadius: 4,
          }],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.parsed.y.toFixed(1)} ${this.$t("timeRegistration.hrs")}`,
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: this.$t("timeRegistration.hrs") },
            },
          },
        },
      });
    },
    buildOvertimeChart() {
      const ctx = document.getElementById("weekly-overtime-chart");
      if (!ctx) return;
      const labels = this.weeklyOvertime.map((w) => w.weekLabel);
      const data = this.weeklyOvertime.map((w) => w.hours);
      const colors = this.weeklyOvertime.map((w) => w.isLastWeek ? "#f57c00" : "#ffcc80");
      this.overtimeChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [{
            label: this.$t("analytics.weeklyOvertimeHours"),
            data,
            backgroundColor: colors,
            borderRadius: 4,
          }],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.parsed.y.toFixed(1)} ${this.$t("timeRegistration.hrs")}`,
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: this.$t("timeRegistration.hrs") },
            },
          },
        },
      });
    },
    buildComplianceChart() {
      const ctx = document.getElementById("compliance-chart");
      if (!ctx) return;
      const labels = this.registrationCompliance.map((w) => w.weekLabel);
      const compliant = this.registrationCompliance.map((w) => w.compliant);
      const nonCompliant = this.registrationCompliance.map((w) => w.nonCompliant);
      // Highlight last week bars with a slightly different shade
      const compliantColors = this.registrationCompliance.map((w) => w.isLastWeek ? "#2e7d32" : "#4caf50");
      const nonCompliantColors = this.registrationCompliance.map((w) => w.isLastWeek ? "#c62828" : "#f44336");
      this.complianceChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: this.$t("analytics.fullyRegistered"),
              data: compliant,
              backgroundColor: compliantColors,
              borderRadius: 4,
              stack: "compliance",
            },
            {
              label: this.$t("analytics.notFullyRegistered"),
              data: nonCompliant,
              backgroundColor: nonCompliantColors,
              borderRadius: 4,
              stack: "compliance",
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}`,
              },
            },
          },
          scales: {
            x: { stacked: true },
            y: {
              stacked: true,
              beginAtZero: true,
              title: { display: true, text: this.$t("analytics.employees") },
              ticks: { stepSize: 1 },
            },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.graph-icon-fit {
  font-size: 1.5rem;
  line-height: 1;
  margin-top: 0.4rem;
}

.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}
</style>
